import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/layout/PageHeader';
import Table from '../../components/ui/Table';
import Spinner from '../../components/ui/Spinner';
import EmptyState from '../../components/ui/EmptyState';
import { fmtDate } from '../../components/requests/RequestActions';
import { listRejectedRequests } from '../../api/rejectedRequests';
import { markRejectedSeen, REJECTED_REQUESTS_COUNT_QUERY_KEY } from '../../hooks/useRejectedRequestCount';

const columns = [
  { key: 'type', label: 'Request', render: (row) => <span className="font-medium">{row.type}</span> },
  {
    key: 'candidates',
    label: 'Candidates',
    render: (row) => (
      <div className="space-y-0.5">
        {row.candidates.map((c) => (
          <div key={c.id}>
            <Link to={`/candidates/${c.id}`} className="text-primary hover:underline">
              {c.full_name}
            </Link>{' '}
            <span className="text-xs text-gray-400">{c.reference_no}</span>
          </div>
        ))}
      </div>
    ),
  },
  { key: 'note', label: 'Your note', render: (row) => row.note || '—' },
  { key: 'requested_at', label: 'Requested', render: (row) => fmtDate(row.requested_at) },
  {
    key: 'rejected',
    label: 'Rejected by',
    render: (row) => (
      <div className="text-xs text-gray-500">
        {row.reviewed_by}
        {row.reviewed_at && <div>{fmtDate(row.reviewed_at)}</div>}
      </div>
    ),
  },
  { key: 'remarks', label: 'Reason', render: (row) => <span className="text-red-700">{row.review_remarks || '—'}</span> },
];

export default function MyRejectedRequests() {
  const query = useQuery({ queryKey: ['rejected-requests'], queryFn: listRejectedRequests });
  const rows = query.data || [];
  const queryClient = useQueryClient();

  // Opening the page counts as seeing everything rejected so far, which clears the sidebar badge.
  useEffect(() => {
    markRejectedSeen();
    queryClient.setQueryData(REJECTED_REQUESTS_COUNT_QUERY_KEY, 0);
  }, [queryClient]);

  return (
    <div>
      <PageHeader title="Rejected Requests" description="Requests you raised that were rejected, with the reviewer's reason." />
      {query.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState title="Nothing rejected" message="None of your requests have been rejected." />
      ) : (
        <Table columns={columns} data={rows} rowKey="key" />
      )}
    </div>
  );
}
